<template>
  <nx-fullscreen-dialog
      :active="active"
      :title="title"
      @dismiss="dismiss"
      classes="nx-clipping-tool-wrapper nx-primary--lighten-4"
      dismissable
  >
    <template slot="toolbar-items">
      <v-btn
          @click.native="accept"
          color="accent"
          flat dark
          v-html="$tc('message.clipping.tool.acceptClippings', value.length)"
          :disabled="cropper.active || !dirty"
      ></v-btn>
    </template>

    <v-container pa-0 pt-1 fluid class="nx-clipping-tool-body">
      <v-layout wrap fill-height>

        <v-flex
            :class="{ md9: value.length > 1 }"
            class="nx-carousel"
        >

          <nx-clipping-tool-carousel
            :content="contentBeingEdited"
            @input="dirty = true"
            ref="carousel"
            v-model="contentBeingEdited.selection"
          ></nx-clipping-tool-carousel>

          <nx-clipping-tool-toolbar
              :clipping="displayedClipping"
              :content="contentBeingEdited"
              @crop="openCropper"
              @input="dirty = true"
              ref="toolbar"
              v-model="contentBeingEdited.selection"
          ></nx-clipping-tool-toolbar>
        </v-flex>

        <v-flex md3 v-if="value.length > 1" class="selection">
          <nx-selectable-list
              :items="items"
              @input="setEditedContent"
          ></nx-selectable-list>
        </v-flex>

        <v-container class="loading-overlay" v-show="loading">
          <v-progress-circular indeterminate></v-progress-circular>
        </v-container>
      </v-layout>

    </v-container>

    <nx-clipping-tool-cropper
        :active="cropper.active"
        :aspectRatios="aspectRatios"
        :src="contentBeingEdited.originalImageUrl"
        :title="$t('message.clipping.clipper.title')"
        @crop="acceptCrop"
        @dismiss="closeCropper"
        ref="cropper"
        v-if="cropper.render"
        v-bind="$attrs"
    ></nx-clipping-tool-cropper>

  </nx-fullscreen-dialog>
</template>

<script>
import api from '@/modules/clipping/api';
import FullscreenDialog from '@/components/dialogs/FullscreenDialog';
import Loadable from '@/mixins/loadable';
import SelectableList from '@/components/list/legacy/SelectableList';
import { mapGetters } from 'vuex';
import Carousel from './components/Carousel';
import Cropper from './components/Cropper';
import Toolbar from './components/Toolbar';

/**
 * Clipping tool.
 *
 * This tool allows the user to select an image clipping, or create a new clipping, based on the
 * allowed aspect ratios given.
 * @property {ClippingCard[]} value -
 * @property {string[]} aspectRatios - Valid aspect ratios for this instance.
 * @property {ClippingCard} clipping - The initial clipping selected. Indicates which content and
 *                                     clipping will be edited initially.
 */
export default {
  name: 'nx-clipping-tool',
  extends: FullscreenDialog,
  mixins: [Loadable],
  components: {
    [Carousel.name]: Carousel,
    [Cropper.name]: Cropper,
    [FullscreenDialog.name]: FullscreenDialog,
    [SelectableList.name]: SelectableList,
    [Toolbar.name]: Toolbar,
  },
  props: {
    aspectRatios: {
      type: Array,
      default: () => [],
    },
    value: {
      type: Array,
      required: true,
      validator: value => value.length > 0,
    },
    clipping: {
      type: Object,
      default: null,
    },
  },
  data() {
    return {
      dirty: false,
      selection: [],
      cropper: {
        render: false,
        active: false,
      },
    };
  },
  computed: {
    ...mapGetters(['availableClippingAspectRatios']),
    aspectRatioAliases() {
      return this.aspectRatios.map(key => this.availableClippingAspectRatios[key].alias);
    },
    contentBeingEdited() {
      return this.selection.find(clipping => clipping.editing) || { originalImageUrl: '' };
    },
    displayedClipping() {
      const selection = this.contentBeingEdited.selection;
      if (selection === undefined) return {};

      return this.contentBeingEdited.clippings[selection];
    },
    items() {
      return this.selection.map(item => ({
        id: item.id,
        title: item.title,
        thumbnailSrc: item.clippings[item.selection].src,
        badges: [{ title: item.type }],
      }));
    },
  },
  watch: {
    active() {
      if (this.active) this.init();
    },
  },
  methods: {
    accept() {
      const selected = this.selection.map(selection => selection.clippings[selection.selection]);
      this.$emit('input', selected);
      this.dismiss();
    },
    async acceptCrop(crop) {
      try {
        const newClipping = await this.createContentClipping(this.contentBeingEdited.id, crop);
        this.contentBeingEdited.clippings.push(newClipping);
        // Give the carousel some time to add the new clipping before selecting it.
        this.$nextTick(() => {
          this.contentBeingEdited.selection = this.contentBeingEdited.clippings.length - 1;
          this.closeCropper();
        });
        this.dirty = true;
      } catch (e) {
        console.error(`Error creating crop for image ${this.contentBeingEdited.id}`, e);
      }
    },
    async createContentClipping(contentId, crop) {
      const payload = { contentId, ...crop };
      return api.createClipping(payload);
    },
    openCropper() {
      console.log('Lets go cropper');
      this.cropper.render = true;
      this.$nextTick(() => { this.cropper.active = true; });
    },
    closeCropper() {
      this.cropper.active = false;
      this.$nextTick(() => { this.cropper.render = false; });
      if (this.$refs.cropper) {
        this.$refs.cropper.reset();
      }
    },
    dismiss() {
      if (this.cropper.active) return;
      this.closeCropper();
      this.$emit('dismiss');
    },
    async init() {
      this.loadingStart();

      // Compute which clipping is being edited initially.
      const initialClippingId = this.clipping && this.clipping.id
        ? this.clipping.id
        : this.value[0].id;

      this.selection = await Promise.all(this.value
        .map(async (clippingCard) => {
          // Fetch the originalImageUrl and clippings for each clipping given.
          const [imageCard, clippings] = await Promise.all([
            api.getImage(clippingCard.image),
            api.clippingSearch(clippingCard.image, this.aspectRatioAliases),
          ]);
          console.log(imageCard);

          // Check which clipping is selected initially. This search shouldn't return -1.
          const selection = clippings.findIndex(clipping => clipping.id === clippingCard.id);
          return {
            ...clippingCard.image,
            originalImageUrl: imageCard.originalImageUrl,
            clippings,
            selection: selection > 0 ? selection : 0,
            editing: clippingCard.id === initialClippingId,
          };
        }));

      this.loadingFinish();
    },
    setEditedContent(item) {
      const selection = this.selection.find(c => c.id === item.id);
      this.contentBeingEdited.editing = false;
      selection.editing = true;
    },
  },
};
</script>

<style lang="stylus">
.nx-clipping-tool-body {
  height: calc(100vh - 64px);

  .nx-carousel {
    position: relative
  }
  .loading-overlay {
    position: absolute
    top: 0
    left: 0
    width: 100%
    height: 100%
    display: flex
    justify-content center
    align-items center
  }
  .v-toolbar {
    position: absolute;
    bottom: 0;
    left: 50%;
    width: auto;
    transform: translateX(-50%) !important;
    transition: bottom .5s;
  }
  .selection {
    overflow-y: auto
    background-color: #f7f7fa
    box-shadow: -1px 0 0 0 rgba(0, 0, 0, 0.1);
    .nx-selectable-list {
      background-color: transparent
    }
  }
}

.nx-clipping-tool-body {
  overflow-y: hidden;
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity .5s;
}

.fade-enter,
.fade-leave-to {
  opacity: 0
  .v-toolbar {
    bottom: -30px;
  }
}
</style>
